import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

export default function GisMiniMap() {
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const w = canvas.width;
    const h = canvas.height;

    // Background
    ctx.fillStyle = "#0f1729";
    ctx.fillRect(0, 0, w, h);

    // Grid lines
    ctx.strokeStyle = "rgba(255,255,255,0.05)";
    ctx.lineWidth = 1;
    for (let x = 0; x < w; x += 30) {
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke();
    }
    for (let y = 0; y < h; y += 30) {
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke();
    }

    // Pipeline network
    const pipes = [
      { x1: 20, y1: 60, x2: 180, y2: 60 },
      { x1: 180, y1: 60, x2: 280, y2: 40 },
      { x1: 180, y1: 60, x2: 200, y2: 130 },
      { x1: 200, y1: 130, x2: 320, y2: 120 },
      { x1: 320, y1: 120, x2: 380, y2: 80 },
      { x1: 50, y1: 60, x2: 60, y2: 140 },
      { x1: 60, y1: 140, x2: 200, y2: 130 },
      { x1: 60, y1: 140, x2: 80, y2: 200 },
      { x1: 80, y1: 200, x2: 250, y2: 190 },
      { x1: 250, y1: 190, x2: 320, y2: 120 },
      { x1: 250, y1: 190, x2: 260, y2: 240 },
      { x1: 260, y1: 240, x2: 380, y2: 230 },
    ];

    pipes.forEach(p => {
      const grad = ctx.createLinearGradient(p.x1, p.y1, p.x2, p.y2);
      grad.addColorStop(0, "rgba(232,83,26,0.6)");
      grad.addColorStop(1, "rgba(232,83,26,0.2)");
      ctx.strokeStyle = grad;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(p.x1, p.y1);
      ctx.lineTo(p.x2, p.y2);
      ctx.stroke();
    });

    // User dots
    const users = [
      { x: 50, y: 55, type: "normal" }, { x: 100, y: 58, type: "normal" },
      { x: 150, y: 62, type: "alarm" }, { x: 200, y: 128, type: "normal" },
      { x: 240, y: 125, type: "normal" }, { x: 300, y: 118, type: "normal" },
      { x: 350, y: 115, type: "alarm" }, { x: 60, y: 138, type: "normal" },
      { x: 120, y: 145, type: "normal" }, { x: 80, y: 198, type: "normal" },
      { x: 150, y: 192, type: "normal" }, { x: 220, y: 188, type: "alarm" },
      { x: 260, y: 238, type: "normal" }, { x: 320, y: 228, type: "normal" },
      { x: 370, y: 78, type: "normal" }, { x: 280, y: 38, type: "commercial" },
    ];

    users.forEach(u => {
      const color = u.type === "alarm" ? "#ef4444" : u.type === "commercial" ? "#f59e0b" : "#22c55e";
      ctx.beginPath();
      ctx.arc(u.x, u.y, u.type === "alarm" ? 4 : 3, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();

      if (u.type === "alarm") {
        ctx.beginPath();
        ctx.arc(u.x, u.y, 7, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(239,68,68,0.4)";
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }
    });

    // Station markers
    const stations = [{ x: 180, y: 60 }, { x: 200, y: 130 }, { x: 320, y: 120 }];
    stations.forEach(s => {
      ctx.beginPath();
      ctx.arc(s.x, s.y, 6, 0, Math.PI * 2);
      ctx.fillStyle = "#E8531A";
      ctx.fill();
      ctx.beginPath();
      ctx.arc(s.x, s.y, 10, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(232,83,26,0.3)";
      ctx.lineWidth = 2;
      ctx.stroke();
    });

    // Legend
    ctx.font = "10px sans-serif";
    const legends = [
      { color: "#22c55e", label: "正常" },
      { color: "#ef4444", label: "告警" },
      { color: "#f59e0b", label: "工商" },
      { color: "#E8531A", label: "调压站" },
    ];
    legends.forEach((l, i) => {
      ctx.beginPath();
      ctx.arc(12 + i * 60, h - 12, 4, 0, Math.PI * 2);
      ctx.fillStyle = l.color;
      ctx.fill();
      ctx.fillStyle = "rgba(255,255,255,0.6)";
      ctx.fillText(l.label, 20 + i * 60, h - 8);
    });
  }, []);

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-map-2-line text-[#E8531A] text-sm"></i>
          </div>
          <span className="text-sm font-semibold text-gray-800">管网GIS概览</span>
          <span className="text-xs text-gray-400">实时更新</span>
        </div>
        <button
          onClick={() => navigate("/gis")}
          className="text-xs text-[#E8531A] hover:underline cursor-pointer whitespace-nowrap"
        >
          查看全图 <i className="ri-arrow-right-line"></i>
        </button>
      </div>
      <div className="relative">
        <canvas ref={canvasRef} width={400} height={260} className="w-full" style={{ height: 200 }} />
        <div className="absolute top-2 right-2 bg-black/60 rounded-lg px-2 py-1">
          <div className="flex items-center gap-1">
            <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></div>
            <span className="text-white text-[10px]">实时</span>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 divide-x divide-gray-100 border-t border-gray-100">
        <div className="px-3 py-2 text-center">
          <div className="text-sm font-bold text-gray-800">142,568</div>
          <div className="text-[10px] text-gray-400">在线设备</div>
        </div>
        <div className="px-3 py-2 text-center">
          <div className="text-sm font-bold text-red-500">23</div>
          <div className="text-[10px] text-gray-400">活跃告警</div>
        </div>
        <div className="px-3 py-2 text-center">
          <div className="text-sm font-bold text-amber-500">3</div>
          <div className="text-[10px] text-gray-400">调压站</div>
        </div>
      </div>
    </div>
  );
}
